"use client";
import { createContext, useState, useContext, useEffect, useMemo } from "react";
import { get_prefix_urls_popups_proxy } from "@core/services/seo";

import {
  set_cookie_client,
  get_cookie_client,
} from "@core/helpers/client/cookie";
import { get_popups_by_keys_query } from "@core/services/CRMs";
import { is_function } from "@core/helpers/function";
import { usePathname } from "next/navigation";
import { strip_locale_prefix } from "@core/helpers/locale-path";

const PopupContext = createContext();

export const usePopupContext = () => useContext(PopupContext);

export const PopupProvider = ({ children, current_locate }) => {
  const [popup_data, set_popup_data] = useState(null);
  const [is_visible, set_is_visible] = useState({});
  const [popup_loading, set_popup_loading] = useState({});
  const [view_count, set_view_count] = useState(
    get_cookie_client("_popup_view_count")
      ? JSON.parse(get_cookie_client("_popup_view_count"))
      : {}
  );

  const pathname = usePathname();

  useEffect(() => {
    const fetch_popup_data = async () => {
      try {
        if (pathname) {
          // only a leading locale segment: `replace("/fa", "")` also cut "/fa" out of "/fashion-news/"
          let _pathname = strip_locale_prefix(pathname, current_locate);

          let result = await get_prefix_urls_popups_proxy({ url: _pathname });

          if (result?.status && result?.data?.found) {
            const _view_count = { ...view_count };
            const _is_visible = {};
            result?.data?.popups?.forEach((item) => {
              if (!_view_count[item?.id] && _view_count[item?.id] !== 0) {
                _view_count[item?.id] = item?.view_count || 2;
              }
              _is_visible[item?.id] = false;
            });
            set_popup_data(result?.data?.popups);
            set_is_visible(_is_visible);
            set_view_count({ ..._view_count });
            get_cookie_client("_popup_view_count", JSON.stringify(_view_count));
          }
        }
      } catch (error) {
        console.error("Error fetching popup data:", error);
      }
    };

    fetch_popup_data();
  }, [pathname]);

  const get_popup_by_key = async (args) => {
    const { success_callback, error_callback, key } = args;
    set_popup_loading({ [key]: true });
    const result = await get_popups_by_keys_query({ keys: [key] });
    if (result?.status) {
      if (is_function(success_callback)) {
        success_callback(result?.data);
      }
    } else {
      if (is_function(error_callback)) {
        error_callback(result?.errors);
      }
    }
    set_popup_loading({ [key]: false });
  };

  const closePopup = (id) => {
    set_is_visible((prev) => ({ ...prev, [id]: false }));
  };

  const show_popup = (id) => {
    if (view_count[id] > 0) {
      set_is_visible((prev) => ({ ...prev, [id]: true }));
      set_view_count((prev) => {
        set_cookie_client(
          "_popup_view_count",
          JSON.stringify({ ...prev, [id]: prev?.[id] - 1 })
        );
        return { ...prev, [id]: prev?.[id] - 1 };
      });
    }
  };

  const value = useMemo(
    () => ({
      popup_data,
      closePopup,
      view_count,
      set_view_count,
      set_is_visible,
      is_visible,
      show_popup,
      get_popup_by_key,
      popup_loading,
    }),
    [popup_data, view_count, is_visible, popup_loading, get_popup_by_key]
  );

  return (
    <PopupContext.Provider value={value}>
      {children}
      {/* <Popup
        content={data}
        is_visible={is_visible}
        onClose={closePopup}
      /> */}
    </PopupContext.Provider>
  );
};
